import { Intervention, InterventionStatus } from '@/app/components/action-plan/types';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import Resources from '@/components/resources/resources';
import TabItem from '@/components/tab-item/tab-item';
import { ROUTE_BUILD_ACTION_PLAN } from '@/constants/routes';
import useIsMobile from '@/hooks/use-is-mobile';
import { useSticky } from '@/hooks/use-sticky';
import { useGetNavbarHeight } from '@/queries/layout.queries';
import { Box, Divider, Flex, Link, Tabs, Text, useBreakpointValue } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';
import ModalCompleteActionItem from '../modals/complete-action-item/complete-action-item-modal';
import CompletedActionPlan from './completed/completed';
import ActionPlanGuidanceBanner from './current-plan/components/action-plan-guidance-banner/action-plan-guidance-banner';
import CurrentActionPlanEmptyState from './current-plan/components/current-action-plan-empty-state';
import CurrentActionPlan from './current-plan/current-plan';
import useDashboardController from './dashboard.controller';

type ActionPlanDashboardProps = {
  interventions: Intervention[];
  onCompleteIntervention: (Intervention: Intervention) => void;
};

export default function ActionPlanDashboard({ interventions, onCompleteIntervention }: ActionPlanDashboardProps) {
  const {
    interventionToComplete,
    completedPercentage,
    handleInterventionCompleted,
    isOpenCompleteModal,
    onCloseCompleteModal,
    filterInterventionsByStatus,
    isActionPlanGuidanceBannerVisible,
    handleCloseActionPlanGuidanceBanner,
    allInterventionsCompleted,
  } = useDashboardController({ interventions, onCompleteIntervention });

  const { data: navBarSize } = useGetNavbarHeight();
  const { ref, isSticky } = useSticky(navBarSize);
  // When using useSticky make sure any child components are wrapped in React.memo at export
  const showCTAButtonInNavbar = useBreakpointValue({ base: false, md: true }, { ssr: false });
  const size = useIsMobile() ? 's' : 'm';
  const isDisabledCompletedTab = completedPercentage === 0;
  const completedInterventions = filterInterventionsByStatus(InterventionStatus.COMPLETED);
  const toDoInterventionsNumber = interventions.length - completedInterventions.length;

  return (
    <>
      <DashboardLayout containerProps={{ pt: 0, pb: 0 }}>
        <Tabs variant="line" isLazy width="100%">
          <Box ref={ref} pt={isSticky ? '50px' : 'inherit'} />
          <Box
            pt={{ base: 8, md: 10, lg: 12 }}
            {...(isSticky
              ? {
                  position: 'fixed',
                  top: `${navBarSize}px`,
                  px: { base: 'spacer-4', md: 'spacer-8', lg: 'spacer-16', xl: 'spacer-24', xxl: 'spacer-32' },
                  left: 0,
                  right: 0,
                  width: '100%',
                  zIndex: 1,
                  bgColor: 'background-page',
                  boxShadow: 'elevation-interactive',
                }
              : {
                  position: 'unset',
                })}
          >
            <Flex
              justifyContent={{ base: 'flex-start', md: 'space-between' }}
              alignItems={{ base: 'center', md: 'end' }}
              maxWidth="1184px"
              margin="0 auto"
            >
              <Tabs.List>
                <TabItem>
                  <Text variant="text-body-meta-medium" textTransform="uppercase" whiteSpace="nowrap">
                    To do ({toDoInterventionsNumber})
                  </Text>
                </TabItem>
                <TabItem isDisabled={isDisabledCompletedTab}>
                  <Text variant="text-body-meta-medium" textTransform="uppercase" whiteSpace="nowrap">
                    Completed ({completedInterventions.length})
                  </Text>
                </TabItem>
              </Tabs.List>
              {isSticky &&
                showCTAButtonInNavbar &&
                (allInterventionsCompleted ? (
                  <Link
                    as={ReactRouterLink}
                    to={ROUTE_BUILD_ACTION_PLAN}
                    variant="accent"
                    size={size}
                    justifyContent="center"
                    flexShrink={0}
                    w="auto"
                    py={{ base: 2, lg: 2 }}
                    px={{ base: 5, lg: 7 }}
                    mb={{ md: 2 }}
                  >
                    <Text variant="text-body-medium">Add new action</Text>
                  </Link>
                ) : (
                  <Link
                    as={ReactRouterLink}
                    to={ROUTE_BUILD_ACTION_PLAN}
                    variant="primary"
                    size={size}
                    justifyContent="center"
                    flexShrink={0}
                    w="auto"
                    py={{ base: 2, lg: 2 }}
                    px={{ base: 5, lg: 7 }}
                    mb={{ md: 2 }}
                  >
                    <Text variant="text-body-medium" color="text-light">
                      Edit Plan
                    </Text>
                  </Link>
                ))}
            </Flex>
          </Box>
          <Divider />
          <DashboardGrid pt={{ base: 5, md: 8, lg: 12 }}>
            <Flex as="main" w="full">
              <Tabs.Panels>
                <Tabs.Panel p="0">
                  {isActionPlanGuidanceBannerVisible && (
                    <ActionPlanGuidanceBanner onCloseBanner={handleCloseActionPlanGuidanceBanner} />
                  )}
                  {allInterventionsCompleted ? (
                    <CurrentActionPlanEmptyState />
                  ) : (
                    <CurrentActionPlan
                      interventions={filterInterventionsByStatus(InterventionStatus.IN_PROGRESS)}
                      onCompleteIntervention={handleInterventionCompleted}
                    />
                  )}
                </Tabs.Panel>
                <Tabs.Panel p="0">
                  <CompletedActionPlan />
                </Tabs.Panel>
              </Tabs.Panels>
            </Flex>
            <Resources as="aside" width="full" />
          </DashboardGrid>
        </Tabs>
      </DashboardLayout>
      {interventionToComplete && (
        <ModalCompleteActionItem
          intervention={interventionToComplete}
          isOpen={isOpenCompleteModal}
          onClose={onCloseCompleteModal}
        />
      )}
    </>
  );
}
